import React, { useEffect, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { v4_add_mailbox_recipients, v4_fetch_mailbox_recipients, v4_mailbox_folders, v4_mailbox_emails, v4_mailbox_email } from '../../actions/v4/mailBox';
import { toast } from 'react-toastify';
import {
    buildAdhocRecipientOption,
    buildRecipientOption,
    filterRecipientOption,
    mergeRecipientOptions,
    normalizeRecipientEmail,
} from './recipientSelectUtils';


const ComposeMail = ({ onSendMail, mailBoxes }) => {
    const [state, setState] = useState({
        fromEmailId: { name: 'fromEmailId', value: '', error: '', isRequired: true },
        email: { name: 'email', value: [], options: [], error: '', isRequired: true },
        subject: { name: 'subject', value: '', error: '', isRequired: true },
        body: { name: 'body', value: '', error: '', isRequired: true },
        cc: { name: 'cc', value: [], options: [], error: '', isRequired: false },
    });

    const [draftsList, setDraftsList] = useState([]);
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [loadingDrafts, setLoadingDrafts] = useState(false);
    const [loadedDraftMessageId, setLoadedDraftMessageId] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const attachmentInputRef = useRef(null);

    const handleAttachmentChange = (e) => {
        const files = Array.from(e.target.files || []);
        setAttachments((prev) => [...prev, ...files]);
        e.target.value = '';
    };

    const removeAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const buildResetState = (prev) => ({
        ...prev,
        fromEmailId: { ...prev.fromEmailId, value: mailBoxes?.[0]?.ID || '', error: '' },
        email: { ...prev.email, value: [], error: '' },
        cc: { ...prev.cc, value: [], error: '' },
        subject: { ...prev.subject, value: '', error: '' },
        body: { ...prev.body, value: '', error: '' },
    });

    const loadDrafts = async () => {
        if (!state.fromEmailId.value) {
            toast.error("Please select a 'Send From' email first.");
            return;
        }
        setLoadingDrafts(true);
        setShowDraftsList(true);
        try {
            const foldersRes = await v4_mailbox_folders(state.fromEmailId.value);
            if (!foldersRes.status) throw new Error(foldersRes.error || 'Failed to load folders');
            const foldersArray = foldersRes.result?.folders || foldersRes.result || [];
            const draftFolder = foldersArray.find(f => String(f.name || f.displayName || f.label || '').toLowerCase() === 'drafts');
            if (!draftFolder) {
                toast.error("Drafts folder not found.");
                setLoadingDrafts(false);
                return;
            }
            const draftFolderId = draftFolder.id || draftFolder.path || draftFolder.name;
            const emailsRes = await v4_mailbox_emails(1, 20, draftFolderId, state.fromEmailId.value);
            if (!emailsRes.status) throw new Error(emailsRes.error || 'Failed to load emails');
            setDraftsList(emailsRes.result?.mails || []);
        } catch (error) {
            toast.error("Failed to load drafts.");
        } finally {
            setLoadingDrafts(false);
        }
    };

    const selectDraft = async (draft) => {
        try {
            const foldersRes = await v4_mailbox_folders(state.fromEmailId.value);
            const foldersArray = foldersRes.result?.folders || foldersRes.result || [];
            const draftFolder = foldersArray.find(f => String(f.name || f.displayName || f.label || '').toLowerCase() === 'drafts');
            const draftFolderId = draftFolder.id || draftFolder.path || draftFolder.name;

            const res = await v4_mailbox_email(draft.messageId, draftFolderId, state.fromEmailId.value);
            if (res.status && res.result?.mail) {
                const mailData = res.result.mail;
                const toEmails = (mailData.to || []).map(e => buildAdhocRecipientOption(typeof e === 'string' ? e : (e.address || e.email)));
                const ccEmails = (mailData.cc || []).map(e => buildAdhocRecipientOption(typeof e === 'string' ? e : (e.address || e.email)));
                
                setState(prev => ({
                    ...prev,
                    email: { ...prev.email, value: toEmails, error: '' },
                    cc: { ...prev.cc, value: ccEmails, error: '' },
                    subject: { ...prev.subject, value: mailData.subject || '', error: '' },
                    body: { ...prev.body, value: mailData.text || mailData.html || '', error: '' },
                }));
                setLoadedDraftMessageId(draft.messageId);
                setShowDraftsList(false);
            }
        } catch (error) {
            toast.error("Failed to load draft content.");
        }
    };

    const fetchRecipientOptions = async () => {
        try {
            const res = await v4_fetch_mailbox_recipients();
            if (!res.status) return;

            const options = (res.result || [])
                .map(buildRecipientOption)
                .filter(Boolean);

            setState((prev) => ({
                ...prev,
                email: {
                    ...prev.email,
                    options: mergeRecipientOptions(options, prev.email.value),
                },
                cc: {
                    ...prev.cc,
                    options: mergeRecipientOptions(options, prev.cc.value),
                },
            }));
        } catch (err) {
            console.error('Failed to fetch mailbox recipients', err);
        }
    };

    useEffect(() => {
        fetchRecipientOptions();
    }, [])

    useEffect(() => {
        const handleRecipientsChanged = () => fetchRecipientOptions();
        window.addEventListener('mailboxRecipientsChanged', handleRecipientsChanged);

        return () => window.removeEventListener('mailboxRecipientsChanged', handleRecipientsChanged);
    }, []);



    useEffect(() => {
        if (!state.fromEmailId.value && mailBoxes?.length > 0) {
            setState((prev) => ({
                ...prev,
                ['fromEmailId']: { ...prev['fromEmailId'], value: mailBoxes[0]['ID'], error: '' },
            }));
        }
    }, [mailBoxes])

    useEffect(() => {
        const composeModal = document.getElementById('composemodal');
        if (!composeModal) return;

        const handleModalShow = () => {
            setState((prev) => buildResetState(prev));
            setShowDraftsList(false);
            setLoadedDraftMessageId(null);
            setAttachments([]);
            fetchRecipientOptions();
        };

        composeModal.addEventListener('show.bs.modal', handleModalShow);
        return () => composeModal.removeEventListener('show.bs.modal', handleModalShow);
    }, [mailBoxes]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({
            ...prev,
            [name]: { ...prev[name], value, error: '' },
        }));
    };

    const handleMultiSelectChange = (selectedOptions, name) => {
        setState((prev) => ({
            ...prev,
            [name]: {
                ...prev[name],
                value: selectedOptions || [],
                error: '',
            },
        }));
    };

    const handleCreateOption = async (inputValue, name) => {
        const email = normalizeRecipientEmail(inputValue);
        if (!email) return;

        const newOption = buildAdhocRecipientOption(email);
        if (!newOption) return;
        const exists = (state.email.options || []).some((opt) => opt.value === email);

        setState((prev) => {
            const currentToValue = Array.isArray(prev.email.value) ? prev.email.value : [];
            const toAlreadySelected = currentToValue.some((opt) => opt.value === email);
            const nextToValue = toAlreadySelected ? currentToValue : [...currentToValue, newOption];

            const currentFieldValue = Array.isArray(prev[name].value) ? prev[name].value : [];
            const fieldAlreadySelected = currentFieldValue.some((opt) => opt.value === email);
            const nextFieldValue = fieldAlreadySelected ? currentFieldValue : [...currentFieldValue, newOption];

            return {
                ...prev,
                [name]: {
                    ...prev[name],
                    value: nextFieldValue,
                    error: '',
                },
                email: {
                    ...prev.email,
                    value: nextToValue,
                    options: mergeRecipientOptions(prev.email.options, [newOption]),
                    error: '',
                },
                cc: { ...prev.cc, options: mergeRecipientOptions(prev.cc.options, [newOption]) },
            };
        });

        if (!exists) {
            try {
                await v4_add_mailbox_recipients({
                    email,
                    name: email.includes('@') ? email.split('@')[0] : email,
                });
            } catch (err) {
                toast.error('Failed to save new email for suggestions.');
            }
        }
    };
    // const handleMultiSelectChange = async (selectedOptions, name) => {
    //     // Identify newly added emails (not in current options list)
    //     const existingOptions = state[name].options || [];
    //     const newEntries = selectedOptions.filter(
    //         opt => !existingOptions.some(o => o.value === opt.value)
    //     );

    //     if (newEntries.length > 0) {
    //         try {
    //             const model = { emails: newEntries.map(e => e.value) }

    //             v4_add_mailbox_recipients(model).then((res) => {
    //                 console.log(res);
    //             })
                
    //         } catch (err) {
    //             console.error('Error saving new recipients:', err);
    //         }
    //     }

    //     // Update state
    //     setState((prev) => ({
    //         ...prev,
    //         [name]: {
    //             ...prev[name],
    //             value: selectedOptions,
    //             error: '',
    //         },
    //     }));
    // };

    const validateForm = (isDraft = false) => {
        debugger
        let valid = true;
        const newState = { ...state };

        Object.keys(newState).forEach((key) => {
            if (!isDraft && newState[key].isRequired && !String(newState[key].value)?.length) {
                newState[key].error = 'This field is required';
                valid = false;
            } else {
                newState[key].error = '';
            }
        });

        setState(newState);
        return valid;
    };

    const resetForm = () => {
        setState((prev) => buildResetState(prev));
        setAttachments([]);
    };


    const handleSend = async (isDraft = false) => {
        debugger
        if (!validateForm(isDraft)) return;

        const toEmails = state.email.value.map((e) => e.value);
        const ccEmails = state.cc.value.map((e) => e.value);

        const payload = {
            to: toEmails,
            subject: state.subject.value,
            text: state.body.value,
            id: state.fromEmailId.value,
            cc: ccEmails,
            isDraft,
            loadedDraftMessageId
        };

        if (attachments.length > 0) {
            if (loadedDraftMessageId && !isDraft) {
                toast.error('Attachments cannot be added when sending a loaded draft.');
                return;
            }
            const formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
                if (value === null || value === undefined) return;
                formData.append(key, Array.isArray(value) ? JSON.stringify(value) : String(value));
            });
            attachments.forEach((file) => formData.append('attachments', file));
            payload.formData = formData;
        }

        try {
            const success = await onSendMail(payload);
            if (success) resetForm(); // ✅ Clear form only on success
        } catch (err) {
            console.error('Send mail error:', err);
            alert('Failed to send mail.');
        }
    };

    return (
        <React.Fragment>
            <div className="modal-body">
                {showDraftsList ? (
                    <div>
                        <div className="d-flex justify-content-between mb-2">
                            <h5>Select a Draft</h5>
                            <button className="btn btn-sm btn-secondary" onClick={() => setShowDraftsList(false)}>Back</button>
                        </div>
                        {loadingDrafts ? <p>Loading drafts...</p> : (
                            draftsList.length === 0 ? <p className="text-muted">No drafts found.</p> : (
                                <div className="list-group" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {draftsList.map(d => (
                                        <button key={d.messageId} type="button" className="list-group-item list-group-item-action" onClick={() => selectDraft(d)}>
                                            <strong>{d.subject || '(no subject)'}</strong><br/>
                                            <small className="text-muted">{d.date ? new Date(d.date).toLocaleString() : ''}</small>
                                        </button>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                    <select
                        isMulti
                        name="fromEmailId"
                        value={state.fromEmailId.value}
                        onChange={handleChange}
                        className="form-select"
                    >
                        <option value={''}> Send From </option>
                        {mailBoxes?.map((mail) => {
                            return (
                                <option value={mail.ID}>{mail.EMAIL} </option>
                            )
                        })}
                    </select>
                    {state.fromEmailId.error && <small className="text-danger">{state.fromEmailId.error}</small>}
                </div>
                <div className="mb-3">
                    <CreatableSelect
                        isMulti
                        name="email"
                        value={state.email.value}
                        options={state.email.options} // Optional: preload some emails if needed
                        filterOption={filterRecipientOption}
                        onChange={(e) => handleMultiSelectChange(e, 'email')}
                        onCreateOption={(value) => handleCreateOption(value, 'email')}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="To"
                        isClearable
                    />
                    {state.email.error && <small className="text-danger">{state.email.error}</small>}
                </div>
                {state.email.value.length > 0 && <div className="mb-3">
                    <CreatableSelect
                        isMulti
                        name="cc"
                        value={state.cc.value}
                        options={state.cc.options}
                        filterOption={filterRecipientOption}
                        onChange={(e) => handleMultiSelectChange(e, 'cc')}
                        onCreateOption={(value) => handleCreateOption(value, 'cc')}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="CC"
                        isClearable
                    />
                    {state.cc.error && <small className="text-danger">{state.cc.error}</small>}
                </div>}
                <div className="mb-3">
                    <input
                        type="text"
                        className="form-control"
                        name="subject"
                        value={state.subject.value}
                        onChange={handleChange}
                        placeholder="Subject"
                    />
                    {state.subject.error && <small className="text-danger">{state.subject.error}</small>}
                </div>
                <div className="mb-3">
                    <textarea
                        className="form-control"
                        rows="5"
                        name="body"
                        value={state.body.value}
                        onChange={handleChange}
                        placeholder="Write your message..."
                    />
                    {state.body.error && <small className="text-danger">{state.body.error}</small>}
                </div>
                <div className="mb-3">
                    <input
                        ref={attachmentInputRef}
                        type="file"
                        multiple
                        className="d-none"
                        onChange={handleAttachmentChange}
                    />
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => attachmentInputRef.current?.click()}
                    >
                        <i className="fas fa-paperclip me-1" /> Attach Files
                    </button>
                    {attachments.length > 0 && (
                        <ul className="list-group mt-2">
                            {attachments.map((file, index) => (
                                <li key={`${file.name}-${index}`} className="list-group-item d-flex justify-content-between align-items-center py-1">
                                    <span className="text-truncate me-2">
                                        <i className="fas fa-file me-2 text-muted" />
                                        {file.name} <small className="text-muted">({formatFileSize(file.size)})</small>
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link text-danger p-0"
                                        onClick={() => removeAttachment(index)}
                                        title="Remove"
                                    >
                                        <i className="fas fa-times" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                    </>
                )}
            </div>
            <div className="modal-footer justify-content-between">
                <div>
                    {!showDraftsList && (
                        <button type="button" className="btn btn-info text-white" onClick={loadDrafts}>
                            <i className="fas fa-folder-open ms-1" /> Load Draft
                        </button>
                    )}
                </div>
                <div>
                <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                >
                    Close
                </button>
                <button type="button" className="btn btn-warning text-white me-2" onClick={() => handleSend(true)}>
                    Save as Draft <i className="fas fa-save ms-1" />
                </button>
                <button type="button" className="btn text-white" style={{ backgroundColor: '#2b5a7e' }} onClick={() => handleSend(false)}>
                    Send <i className="fab fa-telegram-plane ms-1" />
                </button>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ComposeMail;
